import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { Building, Calendar, Users, Save, CheckCircle, XCircle, MessageSquare, ArrowLeft, Filter, AlertCircle, Search, Loader2, ShieldAlert, UserCheck, UserX, RefreshCw, MinusCircle, ChevronDown, ChevronUp, Link2 } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { sansJpa, stagesDesJpa } from '../../../lib/jpa/stages.supabase'
import { useAuth } from '../../hooks/useAuth'
import { useBlockNavigation } from '../../context/NavigationGuardContext'
import { reglementDeLInscription, type LigneDeReglement } from '../../../lib/inscriptions/reglement'
import { lireReglementDesInscriptions } from '../../../lib/inscriptions/reglement.supabase'
import { PastilleDeReglement } from '../../../components/admin/PastilleDeReglement'
import { LiensPresences } from './LiensPresences'

interface Center {
  id: string
  name: string
  address: string
}

interface ChildRegistration {
  registration_id: string
  kid_id: string
  kid_nom: string
  kid_prenom: string
  kid_date_naissance: string
  stage_title: string
  price_type: string
  ecole: string
  school_name?: string
  subgroup_name?: string | null
  user_nom: string
  user_prenom: string
  /** Seul `cancelled` y compte encore : une commande abandonnée. */
  payment_status: string
  cancellation_status: string
  /**
   * Les factures de l'inscription et leur état, lus par
   * `reglement_des_inscriptions` ; `null` si la lecture a échoué. Le statut
   * ne vient plus de `payment_status`, que le circuit des paiements ne tient
   * plus à jour.
   */
  reglement: LigneDeReglement[] | null
  presence?: {
    id: string
    monday_present: boolean | null
    tuesday_present: boolean | null
    wednesday_present: boolean | null
    thursday_present: boolean | null
    friday_present: boolean | null
    notes: string
  }
}

interface DayConfig {
  key: string
  label: string
  shortLabel: string
  date: Date | null
  disabled: boolean
  disabledReason?: string
}

interface PublicHoliday {
  date: string
  name: string
}

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'] as const
type DayKey = typeof DAY_KEYS[number]

const NO_SUBGROUP = '__NO_SUBGROUP__'

interface CenterPresencesProps {
  initialParams?: {
    centerId?: string
    year?: string
    periode?: string
    semaine?: string
  } | null
  onParamsConsumed?: () => void
}

export function CenterPresencesManagement({ initialParams, onParamsConsumed }: CenterPresencesProps = {}) {
  const { userProfile } = useAuth()
  const [centers, setCenters] = useState<Center[]>([])
  const [selectedCenter, setSelectedCenter] = useState<string>('')
  const [years, setYears] = useState<string[]>([])
  const [selectedYear, setSelectedYear] = useState<string>('')
  const [periods, setPeriods] = useState<string[]>([])
  const [selectedPeriod, setSelectedPeriod] = useState<string>('')
  const [weeks, setWeeks] = useState<string[]>([])
  const [selectedWeek, setSelectedWeek] = useState<string>('')
  const [children, setChildren] = useState<ChildRegistration[]>([])
  const [filteredChildren, setFilteredChildren] = useState<ChildRegistration[]>([])
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [sessionStartDate, setSessionStartDate] = useState<string | null>(null)
  const [sessionEndDate, setSessionEndDate] = useState<string | null>(null)
  const [dayConfigs, setDayConfigs] = useState<DayConfig[]>([])
  const [publicHolidays, setPublicHolidays] = useState<PublicHoliday[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [autoInitializing, setAutoInitializing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  useBlockNavigation(hasUnsavedChanges)
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false)
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null)
  const [selectedDay, setSelectedDay] = useState<DayKey | null>(null)
  const [selectedStage, setSelectedStage] = useState<string | null>(null)
  const [selectedSubgroup, setSelectedSubgroup] = useState<string | null>(null)
  const [filtersCollapsed, setFiltersCollapsed] = useState(false)
  const [showCancelled, setShowCancelled] = useState(false)
  // Les liens pour les animateurs (24/10/2026) : la fenêtre s'ouvre depuis l'en-tête.
  const [liensOuverts, setLiensOuverts] = useState(false)


  const fetchRequestId = useRef(0)
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const theadRef = useRef<HTMLTableSectionElement>(null)
  const [stickyHeader, setStickyHeader] = useState(false)
  const lastScrollY = useRef(0)
  const initialParamsRef = useRef(initialParams)
  const paramsApplied = useRef(false)

  useEffect(() => {
    initialParamsRef.current = initialParams
    if (initialParams) {
      paramsApplied.current = false
    }
  }, [initialParams])

  useEffect(() => {
    fetchCenters()
    fetchPublicHolidays()
  }, [])

  // Auto-apply initial params from dashboard tiles
  useEffect(() => {
    const p = initialParamsRef.current
    if (!p || paramsApplied.current) return
    if (p.centerId && centers.length > 0 && !selectedCenter) {
      const found = centers.find(c => c.id === p.centerId)
      if (found) setSelectedCenter(p.centerId)
    }
  }, [centers, selectedCenter])

  useEffect(() => {
    const p = initialParamsRef.current
    if (!p || paramsApplied.current) return
    if (p.year && years.length > 0 && selectedCenter && !selectedYear) {
      if (years.includes(p.year)) setSelectedYear(p.year)
    }
  }, [years, selectedCenter, selectedYear])

  useEffect(() => {
    const p = initialParamsRef.current
    if (!p || paramsApplied.current) return
    if (p.periode && periods.length > 0 && selectedYear && !selectedPeriod) {
      if (periods.includes(p.periode)) setSelectedPeriod(p.periode)
    }
  }, [periods, selectedYear, selectedPeriod])

  useEffect(() => {
    const p = initialParamsRef.current
    if (!p || paramsApplied.current) return
    if (p.semaine && weeks.length > 0 && selectedPeriod && !selectedWeek) {
      if (weeks.includes(p.semaine)) {
        setSelectedWeek(p.semaine)
        paramsApplied.current = true
        onParamsConsumed?.()
      }
    }
  }, [weeks, selectedPeriod, selectedWeek, onParamsConsumed])

  // Sticky header: show on scroll-up, hide on scroll-down
  useEffect(() => {
    const onScroll = () => {
      const currentY = window.scrollY
      const sentinel = tableContainerRef.current
      if (!sentinel) {
        setStickyHeader(false)
        lastScrollY.current = currentY
        return
      }
      const sentinelTop = sentinel.getBoundingClientRect().top
      const scrollingUp = currentY < lastScrollY.current
      if (sentinelTop >= 0) {
        setStickyHeader(false)
      } else if (scrollingUp) {
        setStickyHeader(true)
      } else {
        setStickyHeader(false)
      }
      lastScrollY.current = currentY
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    if (selectedCenter) {
      fetchYears()
    }
  }, [selectedCenter])

  useEffect(() => {
    if (selectedCenter && selectedYear) {
      fetchPeriods()
    }
  }, [selectedCenter, selectedYear])

  useEffect(() => {
    if (selectedCenter && selectedYear && selectedPeriod) {
      fetchWeeks()
    }
  }, [selectedCenter, selectedYear, selectedPeriod])

  useEffect(() => {
    if (selectedCenter && selectedYear && selectedPeriod && selectedWeek) {
      fetchChildren()
    }
  }, [selectedCenter, selectedYear, selectedPeriod, selectedWeek])

  useEffect(() => {
    applySearchFilter()
  }, [children, searchTerm, selectedStage, selectedSubgroup, showCancelled])

  useEffect(() => {
    setSelectedStage(null)
    setSelectedSubgroup(null)
  }, [selectedCenter, selectedYear, selectedPeriod, selectedWeek])

  const fetchPublicHolidays = async () => {
    const { data, error } = await supabase
      .from('public_holidays')
      .select('date, name')
      .order('date')

    if (!error && data) {
      setPublicHolidays(data)
    }
  }

  const fetchCenters = async () => {
    try {
      const { data, error } = await supabase
        .from('centers')
        .select('id, name, address')
        .eq('active', true)
        .order('name')

      if (error) throw error
      setCenters(data || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des centres')
    }
  }

  const fetchYears = async () => {
    try {
      const { data, error } = await sansJpa(supabase
        .from('sessions')
        .select('start_date')
        .eq('center_id', selectedCenter)
        .eq('active', true)
        .order('start_date'), await stagesDesJpa())

      if (error) throw error

      const uniqueYears = [...new Set(
        (data || []).map(s => new Date(s.start_date).getFullYear().toString())
      )].sort()
      setYears(uniqueYears)
      setSelectedYear('')
      setSelectedPeriod('')
      setSelectedWeek('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des années')
    }
  }

  const fetchPeriods = async () => {
    try {
      const { data, error } = await sansJpa(supabase
        .from('sessions')
        .select('periode, start_date')
        .eq('center_id', selectedCenter)
        .eq('active', true)
        .order('periode'), await stagesDesJpa())

      if (error) throw error

      const filtered = (data || []).filter(
        s => new Date(s.start_date).getFullYear().toString() === selectedYear
      )
      const uniquePeriods = [...new Set(filtered.map(s => s.periode))]
      setPeriods(uniquePeriods)
      setSelectedPeriod('')
      setSelectedWeek('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des périodes')
    }
  }

  const fetchWeeks = async () => {
    try {
      const { data, error } = await sansJpa(supabase
        .from('sessions')
        .select('semaine, start_date')
        .eq('center_id', selectedCenter)
        .eq('periode', selectedPeriod)
        .eq('active', true)
        .order('start_date'), await stagesDesJpa())

      if (error) throw error

      const filtered = (data || []).filter(
        s => new Date(s.start_date).getFullYear().toString() === selectedYear
      )
      const uniqueWeeks = [...new Set(filtered.map(s => s.semaine))]
      setWeeks(uniqueWeeks)
      setSelectedWeek('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des semaines')
    }
  }

  const computeDayConfigs = (startDate: string, endDate: string, holidays: PublicHoliday[]): DayConfig[] => {
    const start = new Date(startDate)
    const end = new Date(endDate)

    const monday = new Date(start)
    const dayOfWeek = monday.getDay()
    if (dayOfWeek !== 1) {
      monday.setDate(monday.getDate() - ((dayOfWeek + 6) % 7))
    }

    const dayLabels = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi']
    const dayShortLabels = ['L', 'M', 'Me', 'J', 'V']

    return DAY_KEYS.map((key, i) => {
      const date = new Date(monday)
      date.setDate(monday.getDate() + i)

      const dateStr = date.toISOString().split('T')[0]
      const beforeStart = date < start
      const afterEnd = date > end
      const holiday = holidays.find(h => h.date === dateStr)

      let disabled = false
      let disabledReason: string | undefined

      if (beforeStart || afterEnd) {
        disabled = true
        disabledReason = 'Hors période de la session'
      } else if (holiday) {
        disabled = true
        disabledReason = holiday.name
      }

      return {
        key,
        label: dayLabels[i],
        shortLabel: dayShortLabels[i],
        date,
        disabled,
        disabledReason,
      }
    })
  }

  const fetchChildren = async () => {
    setLoading(true)
    setError(null)
    const currentRequestId = ++fetchRequestId.current

    try {
      const { data: sessionsData, error: sessionsError } = await sansJpa(supabase
        .from('sessions')
        .select(`
          id,
          stage_id,
          periode,
          semaine,
          start_date,
          end_date,
          stages!inner(id, title)
        `)
        .eq('center_id', selectedCenter)
        .eq('periode', selectedPeriod)
        .eq('semaine', selectedWeek)
        .eq('active', true)
        .gte('start_date', `${selectedYear}-01-01`)
        .lte('start_date', `${selectedYear}-12-31`), await stagesDesJpa())

      if (sessionsError) throw sessionsError
      if (currentRequestId !== fetchRequestId.current) return

      if (!sessionsData || sessionsData.length === 0) {
        setChildren([])
        setDayConfigs([])
        setLoading(false)
        return
      }

      const startDate = sessionsData[0].start_date
      const endDate = sessionsData[0].end_date
      setSessionStartDate(startDate)
      setSessionEndDate(endDate)
      setDayConfigs(computeDayConfigs(startDate, endDate, publicHolidays))

      const sessionIds = sessionsData.map(s => s.id)

      const { data: registrationsData, error: regError } = await supabase
        .from('registrations')
        .select(`
          id,
          activity_id,
          kid_id,
          user_id,
          payment_status,
          cancellation_status,
          price_type,
          subgroup:subgroups(id, name),
          subgroup_id,
          kids!inner(id, nom, prenom, date_naissance, ecole),
          users!inner(id, nom, prenom)
        `)
        .in('activity_id', sessionIds)

      if (regError) throw regError
      if (currentRequestId !== fetchRequestId.current) return

      if (!registrationsData || registrationsData.length === 0) {
        setChildren([])
        setLoading(false)
        return
      }

      const registrationIds = registrationsData.map(r => r.id)

      // Lancée avant les présences, attendue après : un échec ne bloque pas la
      // liste, il rend le statut « Indisponible ».
      const reglementsLus = lireReglementDesInscriptions(registrationIds).catch((err) => {
        console.error('[presences] reglement des inscriptions illisible', err)
        return null
      })

      const { data: presencesData, error: presencesError } = await supabase
        .from('presences')
        .select('*')
        .in('registration_id', registrationIds)

      if (presencesError) {
        throw new Error(`Impossible de charger les présences: ${presencesError.message}`)
      }
      const reglements = await reglementsLus
      if (currentRequestId !== fetchRequestId.current) return

      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
      const allSchoolValues = [...new Set(
        registrationsData
          .map(reg => reg.kids.ecole)
          .filter(ecole => ecole && ecole !== 'other' && ecole.length > 0)
      )]
      const schoolIds = allSchoolValues.filter(id => uuidRegex.test(id))

      let schoolsMap: Record<string, string> = {}
      if (schoolIds.length > 0) {
        const { data: schoolsData } = await supabase
          .from('schools')
          .select('id, name')
          .in('id', schoolIds)

        if (schoolsData) {
          schoolsMap = schoolsData.reduce((acc, school) => {
            acc[school.id] = school.name
            return acc
          }, {} as Record<string, string>)
        }
      }
      if (currentRequestId !== fetchRequestId.current) return

      const childrenData: ChildRegistration[] = registrationsData.map((reg, i) => {
        const sessionInfo = sessionsData.find(s => s.id === reg.activity_id)
        const stageTitle = sessionInfo?.stages?.title || 'Stage inconnu'
        const presenceData = presencesData?.find(p => p.registration_id === reg.id)

        let schoolName = '-'
        if (reg.kids.ecole === 'other') {
          schoolName = 'Autre'
        } else if (reg.kids.ecole && schoolsMap[reg.kids.ecole]) {
          schoolName = schoolsMap[reg.kids.ecole]
        } else if (reg.kids.ecole) {
          schoolName = 'École inconnue'
        }

        return {
          registration_id: reg.id,
          kid_id: reg.kids.id,
          kid_nom: reg.kids.nom,
          kid_prenom: reg.kids.prenom,
          kid_date_naissance: reg.kids.date_naissance,
          stage_title: stageTitle,
          price_type: reg.price_type || 'normal',
          ecole: reg.kids.ecole || '',
          school_name: schoolName,
          subgroup_name: reg.subgroup?.name || null,
          user_nom: reg.users.nom || '',
          user_prenom: reg.users.prenom || '',
          payment_status: reg.payment_status,
          cancellation_status: reg.cancellation_status,
          reglement: reglements ? reglements.get(registrationIds[i]) ?? [] : null,
          presence: presenceData ? {
            id: presenceData.id,
            monday_present: presenceData.monday_present,
            tuesday_present: presenceData.tuesday_present,
            wednesday_present: presenceData.wednesday_present,
            thursday_present: presenceData.thursday_present,
            friday_present: presenceData.friday_present,
            notes: presenceData.notes || ''
          } : undefined
        }
      })

      childrenData.sort((a, b) => {
        const nameA = `${a.kid_nom} ${a.kid_prenom}`.toLowerCase()
        const nameB = `${b.kid_nom} ${b.kid_prenom}`.toLowerCase()
        return nameA.localeCompare(nameB)
      })

      setChildren(childrenData)
      if (childrenData.length > 0) setFiltersCollapsed(true)
      setHasUnsavedChanges(false)

      const needsInit = childrenData.filter(c => !c.presence)
      if (needsInit.length > 0) {
        autoInitializePresences(childrenData)
      }
    } catch (err) {
      if (currentRequestId !== fetchRequestId.current) return
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des enfants')
      setChildren([])
    } finally {
      if (currentRequestId === fetchRequestId.current) {
        setLoading(false)
      }
    }
  }

  const isCancelled = (child: ChildRegistration) =>
    child.cancellation_status && child.cancellation_status.startsWith('cancelled_')

  const applySearchFilter = () => {
    let filtered = children

    if (!showCancelled) {
      filtered = filtered.filter(child => !isCancelled(child))
    }

    if (selectedStage !== null) {
      filtered = filtered.filter(child => child.stage_title === selectedStage)
    }

    if (selectedSubgroup !== null) {
      filtered = filtered.filter(child =>
        selectedSubgroup === NO_SUBGROUP
          ? !child.subgroup_name
          : child.subgroup_name === selectedSubgroup
      )
    }

    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase()
      filtered = filtered.filter(child => {
        const childName = `${child.kid_prenom} ${child.kid_nom}`.toLowerCase()
        const parentName = `${child.user_prenom} ${child.user_nom}`.toLowerCase()
        const stageName = child.stage_title.toLowerCase()
        const groupName = (child.subgroup_name || '').toLowerCase()

        return childName.includes(search) ||
          parentName.includes(search) ||
          groupName.includes(search) ||
          stageName.includes(search)
      })
    }

    setFilteredChildren(filtered)
  }

  const availableStages = useMemo(() => {
    const stageNames = new Set<string>()
    children.forEach(child => stageNames.add(child.stage_title))
    return Array.from(stageNames).sort((a, b) => a.localeCompare(b))
  }, [children])

  const availableSubgroups = useMemo(() => {
    const scopedChildren = selectedStage !== null
      ? children.filter(child => child.stage_title === selectedStage)
      : children
    const subgroupNames = new Set<string>()
    let hasNoSubgroup = false
    scopedChildren.forEach(child => {
      if (child.subgroup_name) {
        subgroupNames.add(child.subgroup_name)
      } else {
        hasNoSubgroup = true
      }
    })
    const subgroups = Array.from(subgroupNames).sort((a, b) => a.localeCompare(b))
    return { subgroups, hasNoSubgroup }
  }, [children, selectedStage])

  const autoInitializePresences = async (childrenList: typeof children) => {
    const childrenWithoutPresence = childrenList.filter(c => !c.presence)
    if (childrenWithoutPresence.length === 0) return

    setAutoInitializing(true)
    try {
      const presencesToInsert = childrenWithoutPresence.map(child => ({
        registration_id: child.registration_id,
        monday_present: null,
        tuesday_present: null,
        wednesday_present: null,
        thursday_present: null,
        friday_present: null,
        notes: null,
        recorded_by_user_id: userProfile?.id
      }))

      const { error } = await supabase
        .from('presences')
        .upsert(presencesToInsert, { onConflict: 'registration_id' })

      if (error) throw error

      await fetchChildren()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de l'initialisation automatique des présences")
    } finally {
      setAutoInitializing(false)
    }
  }

  const updatePresence = (registrationId: string, day: string, currentValue: boolean | null) => {
    const dayConfig = dayConfigs.find(d => d.key === day)
    if (dayConfig?.disabled) return

    // Cycle: null → true → false → true → false ...
    let newValue: boolean | null
    if (currentValue === null) {
      newValue = true
    } else if (currentValue === true) {
      newValue = false
    } else {
      newValue = true
    }

    setHasUnsavedChanges(true)
    setChildren(prev => prev.map(child => {
      if (child.registration_id !== registrationId) return child

      const currentPresence = child.presence || {
        id: '',
        monday_present: null,
        tuesday_present: null,
        wednesday_present: null,
        thursday_present: null,
        friday_present: null,
        notes: ''
      }

      return {
        ...child,
        presence: {
          ...currentPresence,
          [`${day}_present`]: newValue
        }
      }
    }))
  }

  const toggleAllForChild = (registrationId: string, present: boolean) => {
    setHasUnsavedChanges(true)
    setChildren(prev => prev.map(child => {
      if (child.registration_id !== registrationId) return child

      return {
        ...child,
        presence: {
          id: child.presence?.id || '',
          monday_present: dayConfigs[0]?.disabled ? null : present,
          tuesday_present: dayConfigs[1]?.disabled ? null : present,
          wednesday_present: dayConfigs[2]?.disabled ? null : present,
          thursday_present: dayConfigs[3]?.disabled ? null : present,
          friday_present: dayConfigs[4]?.disabled ? null : present,
          notes: child.presence?.notes || ''
        }
      }
    }))
  }

  const updateNotes = (registrationId: string, notes: string) => {
    setHasUnsavedChanges(true)
    setChildren(prev => prev.map(child => {
      if (child.registration_id !== registrationId) return child

      const currentPresence = child.presence || {
        id: '',
        monday_present: null as boolean | null,
        tuesday_present: null as boolean | null,
        wednesday_present: null as boolean | null,
        thursday_present: null as boolean | null,
        friday_present: null as boolean | null,
        notes: ''
      }

      return { ...child, presence: { ...currentPresence, notes } }
    }))
  }

  const savePresences = async () => {
    setSaving(true)
    setError(null)
    setSuccessMessage(null)

    try {
      const presencesToSave = children
        .filter(child => child.presence)
        .map(child => ({
          registration_id: child.registration_id,
          monday_present: child.presence!.monday_present,
          tuesday_present: child.presence!.tuesday_present,
          wednesday_present: child.presence!.wednesday_present,
          thursday_present: child.presence!.thursday_present,
          friday_present: child.presence!.friday_present,
          notes: child.presence!.notes || null,
          recorded_by_user_id: userProfile?.id
        }))

      if (presencesToSave.length === 0) {
        setError("Aucune présence à sauvegarder.")
        setSaving(false)
        return
      }

      const { error } = await supabase
        .from('presences')
        .upsert(presencesToSave, { onConflict: 'registration_id' })

      if (error) throw error

      setHasUnsavedChanges(false)
      setSuccessMessage('Présences sauvegardées avec succès !')
      setTimeout(() => setSuccessMessage(null), 3000)
      await fetchChildren()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la sauvegarde')
    } finally {
      setSaving(false)
    }
  }

  const guardSelectionChange = (action: () => void) => {
    if (hasUnsavedChanges) {
      setPendingAction(() => action)
      setShowUnsavedWarning(true)
    } else {
      action()
    }
  }

  const confirmDiscard = () => {
    setShowUnsavedWarning(false)
    setHasUnsavedChanges(false)
    if (pendingAction) {
      pendingAction()
      setPendingAction(null)
    }
  }

  const cancelDiscard = () => {
    setShowUnsavedWarning(false)
    setPendingAction(null)
  }

  const calculateAgeAtSessionStart = (birthDate: string) => {
    if (!sessionStartDate) return 0
    const birth = new Date(birthDate)
    const sessionStart = new Date(sessionStartDate)
    let age = sessionStart.getFullYear() - birth.getFullYear()
    const monthDiff = sessionStart.getMonth() - birth.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && sessionStart.getDate() < birth.getDate())) {
      age--
    }
    return age
  }

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
  }

  const getStatusBadge = (child: ChildRegistration) => (
    <PastilleDeReglement
      statut={reglementDeLInscription(
        { annulation: child.cancellation_status, commande: child.payment_status },
        child.reglement,
      )}
    />
  )

  const resetSelections = () => {
    guardSelectionChange(() => {
      setSelectedCenter('')
      setSelectedYear('')
      setSelectedPeriod('')
      setSelectedWeek('')
      setChildren([])
      setFilteredChildren([])
      setSearchTerm('')
      setDayConfigs([])
    })
  }

  // Stats
  const getDayStats = (dayKey: string) => {
    const config = dayConfigs.find(d => d.key === dayKey)
    if (config?.disabled) return null
    const present = children.filter(c => c.presence?.[`${dayKey}_present` as keyof typeof c.presence] === true).length
    const total = children.filter(c => c.presence?.[`${dayKey}_present` as keyof typeof c.presence] !== null).length
    return { present, total }
  }

  const getTodayDayKey = (): DayKey | null => {
    const dayOfWeek = new Date().getDay()
    const map: Record<number, DayKey> = { 1: 'monday', 2: 'tuesday', 3: 'wednesday', 4: 'thursday', 5: 'friday' }
    return map[dayOfWeek] || null
  }

  const visibleDayConfigs = selectedDay
    ? dayConfigs.filter(d => d.key === selectedDay)
    : dayConfigs

  return (
    <div className="space-y-6">
      {/* Unsaved changes modal */}
      {showUnsavedWarning && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <ShieldAlert className="h-5 w-5 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Modifications non sauvegardées</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Vous avez des modifications de présences non sauvegardées. Si vous continuez, ces modifications seront perdues.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelDiscard}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                Rester sur la page
              </button>
              <button
                onClick={confirmDiscard}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Abandonner les modifications
              </button>
            </div>
          </div>
        </div>
      )}


      {liensOuverts && <LiensPresences onClose={() => setLiensOuverts(false)} />}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Présences du centre</h1>
          <p className="text-gray-600 text-sm sm:text-base">Gestion des présences par centre, période et semaine</p>
        </div>

        <div className="flex items-center space-x-2 sm:space-x-3">
          <button
            onClick={() => setLiensOuverts(true)}
            className="flex items-center space-x-1 sm:space-x-2 px-3 py-2 sm:px-4 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
            title="Un lien par centre pour que les animateurs fassent l'appel sur leur téléphone"
          >
            <Link2 className="h-4 w-4" />
            <span className="hidden sm:inline">Liens pour les anims</span>
            <span className="sm:hidden">Liens</span>
          </button>
          {children.length > 0 && (
            <>
            {hasUnsavedChanges && (
              <span className="text-sm text-amber-600 font-medium flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">Non sauvegardé</span>
                <span className="sm:hidden">!</span>
              </span>
            )}
            <button
              onClick={resetSelections}
              className="flex items-center space-x-1 sm:space-x-2 px-3 py-2 sm:px-4 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Nouvelle sélection</span>
              <span className="sm:hidden">Retour</span>
            </button>
            </>
          )}
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-700 font-medium">Erreur</p>
            <p className="text-red-600 text-sm mt-1">{error}</p>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start">
          <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
          <p className="text-green-700">{successMessage}</p>
        </div>
      )}

      {/* Selection Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Collapsed summary + toggle */}
        {children.length > 0 && filtersCollapsed ? (
          <button
            onClick={() => setFiltersCollapsed(false)}
            className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-2 text-sm text-gray-700 min-w-0">
              <Filter className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <span className="truncate font-medium">
                {centers.find(c => c.id === selectedCenter)?.name}
                {selectedYear && ` \u00b7 ${selectedYear}`}
                {selectedPeriod && ` \u00b7 ${selectedPeriod}`}
                {selectedWeek && ` \u00b7 ${selectedWeek}`}
              </span>
            </div>
            <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
          </button>
        ) : (
          <div className="p-4 sm:p-6">
            {children.length > 0 && (
              <div className="flex items-center justify-end mb-3">
                <button
                  onClick={() => setFiltersCollapsed(true)}
                  className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <span>Masquer</span>
                  <ChevronUp className="h-4 w-4" />
                </button>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Building className="h-4 w-4 inline mr-1" />
                  Centre
                </label>
                <select
                  value={selectedCenter}
                  onChange={(e) => guardSelectionChange(() => {
                    setSelectedCenter(e.target.value)
                    setSelectedYear('')
                    setSelectedPeriod('')
                    setSelectedWeek('')
                    setChildren([])
                    setDayConfigs([])
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Sélectionner un centre</option>
                  {centers.map(center => (
                    <option key={center.id} value={center.id}>{center.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="h-4 w-4 inline mr-1" />
                  Année
                </label>
                <select
                  value={selectedYear}
                  onChange={(e) => guardSelectionChange(() => {
                    setSelectedYear(e.target.value)
                    setSelectedPeriod('')
                    setSelectedWeek('')
                    setChildren([])
                    setDayConfigs([])
                  })}
                  disabled={!selectedCenter}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                >
                  <option value="">Sélectionner une année</option>
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="h-4 w-4 inline mr-1" />
                  Période
                </label>
                <select
                  value={selectedPeriod}
                  onChange={(e) => guardSelectionChange(() => {
                    setSelectedPeriod(e.target.value)
                    setSelectedWeek('')
                    setChildren([])
                    setDayConfigs([])
                  })}
                  disabled={!selectedYear}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                >
                  <option value="">Sélectionner une période</option>
                  {periods.map(period => (
                    <option key={period} value={period}>{period}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Filter className="h-4 w-4 inline mr-1" />
                  Semaine
                </label>
                <select
                  value={selectedWeek}
                  onChange={(e) => guardSelectionChange(() => {
                    setSelectedWeek(e.target.value)
                    setChildren([])
                    setDayConfigs([])
                  })}
                  disabled={!selectedPeriod || !selectedYear}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                >
                  <option value="">Sélectionner une semaine</option>
                  {weeks.map(week => (
                    <option key={week} value={week}>{week}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
          <span className="ml-3 text-gray-600">Chargement des présences...</span>
        </div>
      )}

      {/* Stats + action bar */}
      {!loading && children.length > 0 && (
        <>
          {/* Stats bar */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4">
            <div className="flex items-center justify-between flex-wrap gap-3 sm:gap-4">
              <div className="flex items-center gap-3 sm:gap-6 overflow-x-auto">
                <div className="text-sm flex-shrink-0">
                  <span className="text-gray-500">Inscrits:</span>{' '}
                  <span className="font-semibold text-gray-900">{children.length}</span>
                </div>
                <div className="border-l border-gray-200 pl-3 sm:pl-6 flex items-center gap-2 sm:gap-4 flex-shrink-0">
                  {dayConfigs.map(day => {
                    const stats = getDayStats(day.key)
                    if (!stats) return (
                      <div key={day.key} className="text-center">
                        <div className="text-xs text-gray-400">{day.shortLabel}</div>
                        <div className="text-xs text-gray-300">--</div>
                      </div>
                    )
                    return (
                      <div key={day.key} className="text-center">
                        <div className="text-xs text-gray-500">{day.shortLabel}</div>
                        <div className={`text-sm font-semibold ${stats.present === stats.total && stats.total > 0 ? 'text-green-600' : 'text-blue-600'}`}>
                          {stats.present}/{stats.total}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              <div className="flex items-center space-x-2 sm:space-x-3">
                {hasUnsavedChanges && (
                  <span className="text-sm text-amber-600 font-medium flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    <span className="hidden sm:inline">Non sauvegardé</span>
                    <span className="sm:hidden">!</span>
                  </span>
                )}
                {autoInitializing && (
                  <span className="text-sm text-gray-500 flex items-center">
                    <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                    Chargement...
                  </span>
                )}
                <button
                  onClick={() => guardSelectionChange(() => fetchChildren())}
                  disabled={loading}
                  className="flex items-center space-x-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors font-medium"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  <span>Actualiser</span>
                </button>
              </div>
            </div>
          </div>

          {/* Search + Quick Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un enfant, parent, stage ou groupe..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
            {searchTerm && (
              <p className="text-sm text-gray-600 mt-2">
                {filteredChildren.length} résultat{filteredChildren.length !== 1 ? 's' : ''} sur {children.length}
              </p>
            )}

            {/* Quick filters row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
              {/* Day filter */}
              {dayConfigs.length > 0 && (
                <select
                  value={selectedDay || ''}
                  onChange={(e) => setSelectedDay(e.target.value || null)}
                  className="w-full px-2.5 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="">Semaine complète</option>
                  {(() => {
                    const todayKey = getTodayDayKey()
                    if (todayKey) {
                      const todayConfig = dayConfigs.find(d => d.key === todayKey)
                      if (todayConfig && !todayConfig.disabled) {
                        return <option key="today" value={todayKey}>Aujourd'hui ({todayConfig.shortLabel})</option>
                      }
                    }
                    return null
                  })()}
                  {dayConfigs.filter(d => !d.disabled).map(day => (
                    <option key={day.key} value={day.key}>{day.label}</option>
                  ))}
                </select>
              )}

              {/* Stage filter */}
              {children.length > 0 && availableStages.length > 1 && (
                <select
                  value={selectedStage || ''}
                  onChange={(e) => {
                    setSelectedStage(e.target.value || null)
                    setSelectedSubgroup(null)
                  }}
                  className="w-full px-2.5 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="">Tous les groupes</option>
                  {availableStages.map(stage => (
                    <option key={stage} value={stage}>{stage}</option>
                  ))}
                </select>
              )}

              {/* Subgroup filter */}
              {children.length > 0 && (availableSubgroups.subgroups.length > 0 || availableSubgroups.hasNoSubgroup) && (
                <select
                  value={selectedSubgroup || ''}
                  onChange={(e) => setSelectedSubgroup(e.target.value || null)}
                  className="w-full px-2.5 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="">Tous les sous-groupes</option>
                  {availableSubgroups.subgroups.map(subgroup => (
                    <option key={subgroup} value={subgroup}>{subgroup}</option>
                  ))}
                  {availableSubgroups.hasNoSubgroup && (
                    <option value={NO_SUBGROUP}>Sans sous-groupe</option>
                  )}
                </select>
              )}

              {/* Cancelled toggle */}
              <label className="flex items-center gap-2 px-2.5 py-2 border border-gray-300 rounded-lg text-sm cursor-pointer hover:bg-gray-50 transition-colors col-span-2 sm:col-span-1">
                <input
                  type="checkbox"
                  checked={showCancelled}
                  onChange={(e) => setShowCancelled(e.target.checked)}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-gray-700 truncate">Annulées</span>
              </label>
            </div>
          </div>
        </>
      )}

      {/* Children Table */}
      {!loading && filteredChildren.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 rounded-t-lg">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <Users className="h-5 w-5 mr-2" />
                Liste des enfants ({filteredChildren.length})
              </h2>
              <div className="text-sm text-gray-600">
                {selectedCenter && centers.find(c => c.id === selectedCenter)?.name} - {selectedYear} - {selectedPeriod} - {selectedWeek}
              </div>
            </div>
          </div>

          <div ref={tableContainerRef} />
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead ref={theadRef} className={`bg-gray-50 ${stickyHeader ? 'sticky top-0 z-30 shadow-md' : ''}`}>
                <tr>
                  <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-[31]">
                    <span className="hidden md:inline">Nom</span>
                    <span className="md:hidden">Enfant</span>
                  </th>
                  <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Prénom</th>
                  <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Âge</th>
                  <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Groupe</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden xl:table-cell">Statut</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden xl:table-cell">Tarif</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden xl:table-cell">École</th>
                  {visibleDayConfigs.map(day => (
                    <th key={day.key} className="px-2 sm:px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div>
                        <div className={day.disabled ? 'text-gray-300' : ''}>
                          <span className="hidden sm:inline">{day.label}</span>
                          <span className="sm:hidden">{day.shortLabel || day.label.charAt(0)}</span>
                        </div>
                        {day.date && (
                          <div className={`text-xs font-normal hidden sm:block ${day.disabled ? 'text-gray-300' : 'text-gray-400'}`}>
                            {formatDate(day.date)}
                          </div>
                        )}
                        {day.disabled && day.disabledReason && (
                          <div className="text-xs font-normal text-red-300 truncate max-w-[80px] hidden sm:block" title={day.disabledReason}>
                            {day.disabledReason}
                          </div>
                        )}
                      </div>
                    </th>
                  ))}
                  <th className="px-2 sm:px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                    <span title="Semaine complète">Sem.</span>
                  </th>
                  <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredChildren.map((child) => (
                  <tr key={child.registration_id} className={`transition-colors ${isCancelled(child) ? 'bg-red-50/50 opacity-60' : 'hover:bg-gray-50'}`}>
                    <td className={`px-3 sm:px-4 py-2 sm:py-3 whitespace-nowrap sticky left-0 z-10 ${isCancelled(child) ? 'bg-red-50/50' : 'bg-white'}`}>
                      {/* Mobile: merged name + stage + group */}
                      <div className="md:hidden">
                        <div className="text-sm font-medium text-gray-900">{child.kid_nom} {child.kid_prenom}</div>
                        <div className="text-xs text-gray-500">
                          {child.stage_title}
                          {child.subgroup_name && ` · ${child.subgroup_name}`}
                        </div>
                      </div>
                      {/* Desktop: just last name */}
                      <div className="hidden md:block text-sm font-medium text-gray-900">{child.kid_nom}</div>
                    </td>
                    <td className="px-3 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-sm font-medium text-gray-900 hidden md:table-cell">{child.kid_prenom}</td>
                    <td className="px-3 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-sm text-gray-900 hidden md:table-cell">
                      {calculateAgeAtSessionStart(child.kid_date_naissance)} ans
                    </td>
                    <td className="px-3 sm:px-4 py-2 sm:py-3 whitespace-nowrap hidden md:table-cell">
                      <div className="text-sm text-gray-900">{child.stage_title}</div>
                      {child.subgroup_name && (
                        <div className="text-xs text-gray-500">Groupe: {child.subgroup_name}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap hidden xl:table-cell">
                      {getStatusBadge(child)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap hidden xl:table-cell">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        child.price_type === 'reduced' ? 'bg-orange-100 text-orange-800' :
                        child.price_type === 'local_reduced' || child.price_type === 'local_reduit' ? 'bg-teal-100 text-teal-800' :
                        child.price_type === 'local' ? 'bg-green-100 text-green-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {child.price_type === 'reduced' ? 'Social' :
                         child.price_type === 'local_reduced' || child.price_type === 'local_reduit' ? 'Social local' :
                         child.price_type === 'local' ? 'Local' :
                         'Normal'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 hidden xl:table-cell">{child.school_name}</td>
                    {visibleDayConfigs.map(day => {
                      if (day.disabled) {
                        return (
                          <td key={day.key} className="px-2 sm:px-3 py-2 sm:py-3 text-center bg-gray-50">
                            <div className="inline-flex items-center justify-center w-10 h-10 sm:w-8 sm:h-8 rounded-full bg-gray-200 text-gray-400" title={day.disabledReason}>
                              <span className="text-xs font-medium">--</span>
                            </div>
                          </td>
                        )
                      }

                      const value = child.presence ? (child.presence[`${day.key}_present` as keyof typeof child.presence] as boolean | null) : null
                      return (
                        <td key={day.key} className="px-2 sm:px-3 py-2 sm:py-3 text-center">
                          <button
                            onClick={() => updatePresence(child.registration_id, day.key, value)}
                            className={`inline-flex items-center justify-center w-10 h-10 sm:w-8 sm:h-8 rounded-full transition-all duration-150 ${
                              value === null
                                ? 'bg-gray-100 text-gray-400 hover:bg-gray-200 border border-dashed border-gray-300'
                                : value === true
                                  ? 'bg-green-100 text-green-600 hover:bg-green-200'
                                  : 'bg-red-100 text-red-600 hover:bg-red-200'
                            }`}
                            title={value === null ? 'Non renseigné — cliquez pour marquer présent' : value ? 'Présent — cliquez pour marquer absent' : 'Absent — cliquez pour marquer présent'}
                          >
                            {value === null ? (
                              <MinusCircle className="h-5 w-5" />
                            ) : value ? (
                              <CheckCircle className="h-5 w-5" />
                            ) : (
                              <XCircle className="h-5 w-5" />
                            )}
                          </button>
                        </td>
                      )
                    })}
                    <td className="px-2 sm:px-3 py-2 sm:py-3 text-center">
                      <div className="flex flex-col items-center space-y-1">
                        <button
                          onClick={() => toggleAllForChild(child.registration_id, true)}
                          className="text-green-500 hover:text-green-700 transition-colors"
                          title="Marquer présent toute la semaine"
                        >
                          <UserCheck className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => toggleAllForChild(child.registration_id, false)}
                          className="text-red-400 hover:text-red-600 transition-colors"
                          title="Marquer absent toute la semaine"
                        >
                          <UserX className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                    <td className="px-3 sm:px-4 py-2 sm:py-3">
                      <div className="flex items-center space-x-2">
                        <MessageSquare className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <input
                          type="text"
                          value={child.presence?.notes || ''}
                          onChange={(e) => updateNotes(child.registration_id, e.target.value)}
                          placeholder="Note..."
                          className="flex-1 text-sm border-0 border-b border-gray-200 focus:border-blue-500 focus:ring-0 bg-transparent min-w-[100px] sm:min-w-[120px]"
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Bottom spacer so floating save button never covers last rows */}
          <div className="h-20 sm:h-16" />

        </div>
      )}

      {/* Empty states */}
      {!loading && children.length === 0 && selectedCenter && selectedPeriod && selectedWeek && (
        <div className="text-center py-12">
          <Users className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun enfant inscrit</h3>
          <p className="mt-1 text-sm text-gray-500">
            Aucun enfant inscrit pour cette sélection.
          </p>
        </div>
      )}

      {!loading && searchTerm && filteredChildren.length === 0 && children.length > 0 && (
        <div className="text-center py-12">
          <Search className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun résultat</h3>
          <p className="mt-1 text-sm text-gray-500">
            Aucun enfant ne correspond à votre recherche "{searchTerm}".
          </p>
        </div>
      )}

      {/* Floating save button */}
      {!loading && children.length > 0 && hasUnsavedChanges && (
        <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-40">
          <button
            onClick={savePresences}
            disabled={saving}
            className="flex items-center space-x-2 px-5 py-3.5 sm:px-6 sm:py-3.5 bg-blue-600 text-white rounded-full shadow-2xl hover:bg-blue-700 disabled:opacity-50 transition-all duration-200 hover:scale-105 font-medium text-sm sm:text-base"
          >
            {saving ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Save className="h-5 w-5" />
            )}
            <span>{saving ? 'Sauvegarde...' : 'Sauvegarder'}</span>
          </button>
        </div>
      )}
    </div>
  )
}
